import { useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  Spinner,
  cn,
  type PartnerMemberOut,
  type PartnerRole,
} from "@dearbill/shared";
import { roleChangeErrorMessage } from "../routes/memberErrors";

/**
 * Each role's pill label, one-line description and pill classes. The pill
 * color is decorative; the label carries the meaning.
 */
const ROLE_DISPLAY: Record<PartnerRole, { label: string; detail: string; pillClass: string }> = {
  org_admin: {
    label: "Admin",
    detail: "Full access, including adding & removing people",
    pillClass: "bg-frost text-deep-navy",
  },
  user: {
    label: "Referrer",
    detail: "Refer patients & check costs",
    pillClass: "bg-ice text-deep-navy",
  },
};

/** The other role. With exactly two roles, "Change role" is a toggle. */
const OTHER_ROLE: Record<PartnerRole, PartnerRole> = { user: "org_admin", org_admin: "user" };

const SELF_HINT = "You can't change your own role.";

interface MemberRowProps {
  member: PartnerMemberOut;
  /** True for the caller's own row, whose role and remove controls are disabled. */
  isSelf: boolean;
  /** Resolves with the server's updated member; rejection leaves the old role. */
  onRoleChange: (role: PartnerRole) => Promise<void>;
  onRemove: () => void;
}

/**
 * One row of the Team table: the name over the phone (mono, per the Figma
 * table, dR8cJ6hRSW5eERToo64boe node 2340:653) with the email as the fallback
 * for either, the role pill and description, and the "Change role" and
 * "Remove access" actions. A member who hasn't set up the account yet gets an
 * "Invited, not set up yet" line, and "Remove access" also cancels the invite.
 *
 * "Change role" toggles between the two roles behind a confirm prompt. The row
 * owns its role-save state, so a busy role change on one row never disables
 * the others. The parent keeps the authoritative member list and supplies the
 * action callbacks.
 *
 * The owner's row carries an Owner badge beside the name and no actions,
 * because the owner's role is fixed (the server answers 403). On any other row
 * `isSelf` disables both actions; the server also rejects self-targeted
 * changes with a 400.
 */
export function MemberRow({ member, isSelf, onRoleChange, onRemove }: MemberRowProps): ReactNode {
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  const pending = member.status !== "active";
  const display = ROLE_DISPLAY[member.role];
  const nextRole = OTHER_ROLE[member.role];

  function handleChangeRole(): void {
    if (roleSaving) return;
    const label = member.name ?? member.email;
    const nextLabel = ROLE_DISPLAY[nextRole].label;
    if (!window.confirm(`Change ${label}'s role to ${nextLabel}?`)) return;
    setRoleSaving(true);
    setRoleError(null);
    onRoleChange(nextRole)
      .catch((err: unknown) => setRoleError(roleChangeErrorMessage(err)))
      .finally(() => setRoleSaving(false));
  }

  return (
    <tr className={cn("border-haze border-t", pending && "bg-lemon")}>
      <td className="py-4 pl-6 pr-4 align-top">
        <p className="text-foreground flex flex-wrap items-center gap-2 text-[17px] font-semibold leading-6">
          {/* A nameless (invited) member reads as their email, the identity
              the invite went to. */}
          <span>
            {member.name ?? member.email}
            {isSelf && " (you)"}
          </span>
          {member.isOwner && <Badge tone="neutral">Owner</Badge>}
        </p>
        {/* The phone identifies the row like the design's table; a member with
            no phone yet (not set up) falls back to the email. */}
        {member.phone !== "" ? (
          <p className="text-slate-teal mt-0.5 font-mono text-sm leading-5">{member.phone}</p>
        ) : (
          member.name !== null && (
            <p className="text-slate-teal mt-0.5 text-sm leading-5">{member.email}</p>
          )
        )}
        {pending && (
          <p className="text-ochre mt-0.5 text-sm font-semibold leading-5">
            Invited, not set up yet
          </p>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-3 py-[5px] text-[13px] font-bold leading-[18px]",
            display.pillClass,
          )}
        >
          {display.label}
        </span>
        <p className="text-slate-teal mt-1.5 text-sm leading-5">{display.detail}</p>
        {roleError && <p className="text-danger mt-1 text-sm">{roleError}</p>}
      </td>
      <td className="py-4 pl-4 pr-6 text-right align-top">
        {!member.isOwner && (
          <div className="inline-flex items-center gap-3">
            {roleSaving && (
              <span className="inline-flex shrink-0">
                <Spinner />
              </span>
            )}
            <Button
              variant="outlined"
              size="sm"
              onClick={handleChangeRole}
              disabled={isSelf || roleSaving}
              aria-label={`Change role for ${member.email}`}
              title={isSelf ? SELF_HINT : undefined}
            >
              Change role
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={onRemove}
              disabled={isSelf}
              aria-label={`Remove access for ${member.email}`}
              title={isSelf ? "You can't remove yourself." : undefined}
            >
              Remove access
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}
